/**
 * @fileoverview Badge - Small status and label component
 *
 * Purpose: Display status indicators, labels, counts, and tags
 * Dependencies: class-variance-authority for variants
 * Used by: Status indicators, notification counts, tags, labels throughout the app
 *
 * Features:
 * - Multiple variants (default, secondary, destructive, outline, soft, solid)
 * - Color system with 13 colors × 3 color-aware variants
 * - Three sizes (sm, md, lg)
 * - Rounded-rectangle shape
 * - Light + dark mode support
 *
 * @example
 * <Badge>New</Badge>
 * <Badge variant="destructive">Error</Badge>
 * <Badge color="emerald" variant="soft">Ready</Badge>
 * <Badge size="sm" color="amber">Warning</Badge>
 */

import * as React from "react";
import { type VariantProps } from "class-variance-authority";

import { cn } from "@/utils/misc";
import { badgeVariants } from "./badge.variants";

// ---------------------------------------------------------------------------
// Color system
// ---------------------------------------------------------------------------

const BADGE_COLORS = [
  "gray",
  "blue",
  "red",
  "green",
  "emerald",
  "amber",
  "teal",
  "indigo",
  "purple",
  "pink",
  "orange",
  "violet",
  "sky",
] as const;

export type BadgeColor = (typeof BADGE_COLORS)[number];

/**
 * Color map: each color defines classes for the three color-aware variants.
 * All entries include both light and dark mode classes.
 */
const colorMap: Record<
  BadgeColor,
  Record<"soft" | "outline" | "solid", string>
> = {
  gray: {
    soft: "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700",
    outline:
      "bg-transparent text-gray-700 border-gray-300 dark:text-gray-300 dark:border-gray-600",
    solid: "bg-gray-600 text-white border-transparent dark:bg-gray-500",
  },
  blue: {
    soft: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-400 dark:border-blue-800",
    outline:
      "bg-transparent text-blue-700 border-blue-300 dark:text-blue-400 dark:border-blue-700",
    solid: "bg-blue-600 text-white border-transparent dark:bg-blue-500",
  },
  red: {
    soft: "bg-red-100 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-400 dark:border-red-800",
    outline:
      "bg-transparent text-red-700 border-red-300 dark:text-red-400 dark:border-red-700",
    solid: "bg-red-600 text-white border-transparent dark:bg-red-500",
  },
  green: {
    soft: "bg-green-100 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-400 dark:border-green-800",
    outline:
      "bg-transparent text-green-700 border-green-300 dark:text-green-400 dark:border-green-700",
    solid: "bg-green-600 text-white border-transparent dark:bg-green-500",
  },
  emerald: {
    soft: "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-400 dark:border-emerald-800",
    outline:
      "bg-transparent text-emerald-700 border-emerald-300 dark:text-emerald-400 dark:border-emerald-700",
    solid: "bg-emerald-600 text-white border-transparent dark:bg-emerald-500",
  },
  amber: {
    soft: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-800",
    outline:
      "bg-transparent text-amber-700 border-amber-300 dark:text-amber-400 dark:border-amber-700",
    solid: "bg-amber-600 text-white border-transparent dark:bg-amber-500",
  },
  teal: {
    soft: "bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-950 dark:text-teal-400 dark:border-teal-800",
    outline:
      "bg-transparent text-teal-700 border-teal-300 dark:text-teal-400 dark:border-teal-700",
    solid: "bg-teal-600 text-white border-transparent dark:bg-teal-500",
  },
  indigo: {
    soft: "bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-950 dark:text-indigo-400 dark:border-indigo-800",
    outline:
      "bg-transparent text-indigo-700 border-indigo-300 dark:text-indigo-400 dark:border-indigo-700",
    solid: "bg-indigo-600 text-white border-transparent dark:bg-indigo-500",
  },
  purple: {
    soft: "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-400 dark:border-purple-800",
    outline:
      "bg-transparent text-purple-700 border-purple-300 dark:text-purple-400 dark:border-purple-700",
    solid: "bg-purple-600 text-white border-transparent dark:bg-purple-500",
  },
  pink: {
    soft: "bg-pink-100 text-pink-700 border-pink-200 dark:bg-pink-950 dark:text-pink-400 dark:border-pink-800",
    outline:
      "bg-transparent text-pink-700 border-pink-300 dark:text-pink-400 dark:border-pink-700",
    solid: "bg-pink-600 text-white border-transparent dark:bg-pink-500",
  },
  orange: {
    soft: "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-400 dark:border-orange-800",
    outline:
      "bg-transparent text-orange-700 border-orange-300 dark:text-orange-400 dark:border-orange-700",
    solid: "bg-orange-600 text-white border-transparent dark:bg-orange-500",
  },
  violet: {
    soft: "bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-950 dark:text-violet-400 dark:border-violet-800",
    outline:
      "bg-transparent text-violet-700 border-violet-300 dark:text-violet-400 dark:border-violet-700",
    solid: "bg-violet-600 text-white border-transparent dark:bg-violet-500",
  },
  sky: {
    soft: "bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-950 dark:text-sky-400 dark:border-sky-800",
    outline:
      "bg-transparent text-sky-700 border-sky-300 dark:text-sky-400 dark:border-sky-700",
    solid: "bg-sky-600 text-white border-transparent dark:bg-sky-500",
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  color?: BadgeColor;
}

function Badge({ className, variant, size, color, ...props }: BadgeProps) {
  const resolvedColorEntry = color ? colorMap[color] : undefined;

  // Resolve the effective variant for color mapping
  const colorVariant =
    variant === "soft" || variant === "solid"
      ? variant
      : variant === "outline" && color
        ? "outline"
        : null;

  const colorClasses =
    resolvedColorEntry && colorVariant
      ? resolvedColorEntry[colorVariant]
      : undefined;

  // When color is provided without an explicit color-aware variant, default to "soft"
  const effectiveColorClasses =
    resolvedColorEntry && !colorVariant
      ? resolvedColorEntry.soft
      : colorClasses;

  // When color is used with legacy variant names, override to soft
  const effectiveVariant =
    resolvedColorEntry && !colorVariant ? "soft" : variant;

  return (
    <div
      className={cn(
        badgeVariants({ variant: effectiveVariant, size }),
        effectiveColorClasses,
        className,
      )}
      {...props}
    />
  );
}

export { Badge };
